/**
 * Genesys WebView Proxy Utility
 * 
 * This utility intercepts Genesys API calls that fail due to CORS in Flutter WebView
 * and routes them through our server-side proxy endpoints.
 */

interface GenesysApiCall {
  url: string
  method?: string
  headers?: Record<string, string>
  body?: any
}

/**
 * Detects if we're running in a Flutter WebView environment
 */
export const isFlutterWebView = (): boolean => {
  if (typeof window === 'undefined') return false
  
  const userAgent = window.navigator.userAgent
  const hasFlutterIndicators = 
    userAgent.includes('OnePassMobile') ||
    userAgent.includes('OnePassApp/Flutter') ||
    userAgent.includes('Flutter') ||
    // Check for custom headers that indicate Flutter WebView
    (window as any).flutter_inappwebview !== undefined
  
  return hasFlutterIndicators
}

/**
 * Intercepts XMLHttpRequest for Genesys API calls in Flutter WebView
 */
export const setupGenesysWebViewInterceptor = () => {
  if (!isFlutterWebView()) {
    console.log('[Genesys WebView] Not in Flutter WebView, skipping interceptor')
    return
  }
  
  console.log('[Genesys WebView] Setting up CORS interceptor for Flutter WebView')
  
  // Store original XMLHttpRequest
  const OriginalXMLHttpRequest = window.XMLHttpRequest
  
  // Create custom XMLHttpRequest that proxies Genesys calls
  window.XMLHttpRequest = function() {
    const xhr = new OriginalXMLHttpRequest()
    const originalOpen = xhr.open
    const originalSend = xhr.send
    
    let requestUrl = ''
    let requestMethod = 'GET'
    let requestHeaders: Record<string, string> = {}
    
    // Override open method to capture request details
    xhr.open = function(method: string, url: string | URL, async: boolean = true, username?: string | null, password?: string | null) {
      requestUrl = url.toString()
      requestMethod = method.toUpperCase()
      
      // Check if this is a Genesys API call that needs proxying
      if (requestUrl.includes('mypurecloud.com.au') && requestUrl.includes('/webdeployments/')) {
        console.log('[Genesys WebView] Intercepting Genesys API call:', requestUrl)
        
        // Don't call original open for intercepted requests
        return
      }
      
      // Call original open for non-Genesys requests
      return originalOpen.apply(this, [method, url, async, username, password])
    }
    
    // Override setRequestHeader to capture headers
    const originalSetRequestHeader = xhr.setRequestHeader
    xhr.setRequestHeader = function(name: string, value: string) {
      requestHeaders[name] = value
      
      // Only set header if not intercepted
      if (!requestUrl.includes('mypurecloud.com.au')) {
        return originalSetRequestHeader.apply(this, [name, value])
      }
    }
    
    // Override send method to proxy Genesys calls
    xhr.send = function(body?: any) {
      if (requestUrl.includes('mypurecloud.com.au') && requestUrl.includes('/webdeployments/')) {
        console.log('[Genesys WebView] Proxying Genesys API call through server')
        
        // Make the request through our proxy
        const proxyRequest: GenesysApiCall = {
          url: requestUrl,
          method: requestMethod,
          headers: requestHeaders,
          body: body
        }
        
        // Create a fresh request each time to prevent "Request object already used" errors
        // This is critical for Flutter WebView environments that may retry requests
        const makeProxyRequest = () => {
          return fetch('/api/genesys/proxy', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(proxyRequest),
            // Critical: Disable any automatic retries to prevent Request object reuse
            // Flutter WebView can be sensitive to request reuse
            cache: 'no-cache',
            credentials: 'same-origin'
          })
        }
        
        // Execute the proxy request with proper error handling for WebView
        makeProxyRequest()
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
          }
          return response.json()
        })
        .then(data => {
          // Simulate successful XMLHttpRequest response
          Object.defineProperty(xhr, 'status', { value: 200, writable: false })
          Object.defineProperty(xhr, 'statusText', { value: 'OK', writable: false })
          Object.defineProperty(xhr, 'response', { value: JSON.stringify(data), writable: false })
          Object.defineProperty(xhr, 'responseText', { value: JSON.stringify(data), writable: false })
          Object.defineProperty(xhr, 'readyState', { value: 4, writable: false })
          
          // Trigger success events
          if (xhr.onreadystatechange) {
            xhr.onreadystatechange(new Event('readystatechange') as any)
          }
          if (xhr.onload) {
            xhr.onload({} as ProgressEvent)
          }
        })
        .catch(error => {
          console.error('[Genesys WebView] Proxy request failed:', error)
          
          // Enhanced error handling for Flutter WebView
          let errorMessage = 'Network Error'
          if (error.message && error.message.includes('Request object already used')) {
            console.warn('[Genesys WebView] Detected Request reuse error - this is a known Flutter WebView issue')
            errorMessage = 'Request Processing Error'
          }
          
          // Simulate error response
          Object.defineProperty(xhr, 'status', { value: 0, writable: false })
          Object.defineProperty(xhr, 'statusText', { value: errorMessage, writable: false })
          Object.defineProperty(xhr, 'readyState', { value: 4, writable: false })
          
          // Trigger error events
          if (xhr.onerror) {
            xhr.onerror({} as ProgressEvent)
          }
        })
        
        return
      }
      
      // Call original send for non-Genesys requests
      return originalSend.apply(this, [body])
    }
    
    return xhr
  } as any
  
  // Copy static properties
  Object.setPrototypeOf(window.XMLHttpRequest, OriginalXMLHttpRequest)
  Object.setPrototypeOf(window.XMLHttpRequest.prototype, OriginalXMLHttpRequest.prototype)
}

/**
 * Initialize Genesys with WebView compatibility
 */
export const initializeGenesysWithWebViewSupport = async () => {
  // Set up interceptor before Genesys loads
  setupGenesysWebViewInterceptor()
  
  try {
    // Get configuration from our secure endpoint
    const configResponse = await fetch('/api/genesys/config')
    if (!configResponse.ok) {
      throw new Error(`Failed to get Genesys config: ${configResponse.statusText}`)
    }
    
    const genesysConfig = await configResponse.json()
    console.log('[Genesys WebView] Loaded configuration:', genesysConfig)
    
    // Create Genesys script with our configuration
    const genesysScript = document.createElement("script")
    genesysScript.type = "text/javascript"
    genesysScript.charset = "utf-8"
    
    genesysScript.innerHTML = `
      (function (g, e, n, es, ys) {
        g['_genesysJs'] = e;
        g[e] = g[e] || function () {
          (g[e].q = g[e].q || []).push(arguments)
        };
        g[e].t = 1 * new Date();
        g[e].c = es;
        ys = document.createElement('script'); ys.async = 1; ys.src = n; ys.charset = 'utf-8'; document.head.appendChild(ys);
      })(window, 'Genesys', 'https://apps.mypurecloud.com.au/genesys-bootstrap/genesys.min.js', {
        environment: '${genesysConfig.environment}',
        deploymentId: '${genesysConfig.deploymentId}'
      });
    `
    
    document.head.appendChild(genesysScript)
    console.log('[Genesys WebView] Script initialized with WebView support')
    
  } catch (error) {
    console.error('[Genesys WebView] Failed to initialize:', error)
  }
}
